import LeaderboardOutlinedIcon from '@mui/icons-material/LeaderboardOutlined';
import { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ApiEntityStatistics } from '../../../../api/hexa/ApiEntityStatistics';
import {
  DateRangeFilter,
  LastUpdatedChip,
  ResultsCountFilter,
  TagsFilter,
} from '../StatisticsFilters';
import StatisticsRankedList from '../StatisticsRankedList';
import StatisticsSection from '../StatisticsSection';
import TooltipLink, { API_DOCS_URLS } from '../TooltipLink';
import { useMostSearchedStats } from '../useMostSearchedStats';

interface MonitoredNamesTabProps {
  onFetchError: (message: string) => void;
}

const MonitoredNamesTab = ({ onFetchError }: MonitoredNamesTabProps) => {
  const { t } = useTranslation();
  /** undefined until the first response arrives, null if the last extraction failed */
  const [lastUpdated, setLastUpdated] = useState<string | null | undefined>(
    undefined
  );

  const fetchMonitoredNames = useCallback(
    (params: Parameters<typeof ApiEntityStatistics.mostMonitoredNames>[0]) =>
      ApiEntityStatistics.mostMonitoredNames(params).then((response) => {
        setLastUpdated(response.last_updated ?? null);
        return response.most_monitored;
      }),
    []
  );

  const monitoredNamesStats = useMostSearchedStats(
    fetchMonitoredNames,
    onFetchError
  );

  return (
    <StatisticsSection
      title={t('entity_statistics.monitored_names_title')}
      subtitle={t('entity_statistics.monitored_names_subtitle')}
      subtitleTooltip={
        <Trans
          i18nKey="entity_statistics.monitored_names_subtitle_tooltip"
          components={{
            monitoringLink: <TooltipLink href={API_DOCS_URLS.monitoring} />,
          }}
        />
      }
      icon={<LeaderboardOutlinedIcon />}
      headerAdornment={
        <>
          {lastUpdated !== undefined && (
            <LastUpdatedChip
              lastUpdated={lastUpdated}
              tooltip={(updateTime) =>
                t('entity_statistics.monitored_names_update_tooltip', {
                  time: updateTime,
                })
              }
            />
          )}
          </>
      }
      filtersTitle={t('entity_statistics.filters')}
      onResetFilters={monitoredNamesStats.resetFilters}
      isLoading={monitoredNamesStats.isLoading}
      loadingText={t('entity_statistics.monitored_names_loading')}
      isEmpty={monitoredNamesStats.results.length === 0}
      emptyText={t('entity_statistics.monitored_names_empty')}
      filters={
        <>
          <DateRangeFilter state={monitoredNamesStats} />
          <ResultsCountFilter state={monitoredNamesStats} />
          <TagsFilter state={monitoredNamesStats} />
        </>
      }
    >
      <StatisticsRankedList
        valueLabel={t('entity_statistics.monitored_count')}
        entries={monitoredNamesStats.results.map((entry, index) => ({
          id: `${entry.name}-${index}`,
          label: entry.name,
          value: entry.monitored_count,
        }))}
      />
    </StatisticsSection>
  );
};

export default MonitoredNamesTab;
